// Import necessary React hooks and Material-UI components
import React, { useState, useCallback } from "react";
import {
  Box,
  Grid,
  Typography,
  Button,
  Modal,
  Backdrop,
  IconButton,
} from "@mui/material";
import WestIcon from "@mui/icons-material/West";
import EastIcon from "@mui/icons-material/East";
import CloseIcon from "@mui/icons-material/Close";
import { trasformationImages } from "../../../config/config"; // Adjust the import path as necessary

// Define an array of image objects with source and alt text
const images = [
  { src: trasformationImages.transformation01, alt: "Transformation 1" },
  { src: trasformationImages.transformation02, alt: "Transformation 2" },
  { src: trasformationImages.transformation007, alt: "Transformation 7" },
  { src: trasformationImages.transformation008, alt: "Transformation 8" },
];

// RealResult component to display a gallery of transformation images
const RealResult = () => {
  // State to manage the selected image, gallery visibility, and current image index
  const [selectedImage, setSelectedImage] = useState(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Function to open the gallery modal with the selected image
  const openGallery = (index) => {
    setCurrentImageIndex(index);
    setSelectedImage(images[index]);
    setGalleryOpen(true);
  };

  // Function to close the gallery modal
  const closeGallery = () => {
    setGalleryOpen(false);
    setSelectedImage(null);
  };

  // Function to navigate to the next image in the gallery
  const nextImage = useCallback(() => {
    const nextIndex = (currentImageIndex + 1) % images.length;
    setCurrentImageIndex(nextIndex);
    setSelectedImage(images[nextIndex]);
  }, [currentImageIndex]);

  // Function to navigate to the previous image in the gallery
  const prevImage = useCallback(() => {
    const prevIndex =
      currentImageIndex === 0 ? images.length - 1 : currentImageIndex - 1;
    setCurrentImageIndex(prevIndex);
    setSelectedImage(images[prevIndex]);
  }, [currentImageIndex]);

  // Effect to handle keyboard navigation for the gallery modal
  React.useEffect(() => {
    const handleKeyDown = (event) => {
      if (!galleryOpen) return;
      
      if (event.key === 'ArrowLeft') {
        prevImage();
      } else if (event.key === 'ArrowRight') {
        nextImage();
      } else if (event.key === 'Escape') {
        closeGallery();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [galleryOpen, currentImageIndex, nextImage, prevImage]);

  return (
    // Main container for the RealResult section
    <div className="section-04">
      <Box sx={{ backgroundColor: "#111111", py: { xs: 6, md: 6 } }}>
        <Box
          className="container"
          sx={{
            display: "flex",
            justifyContent: "center",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {/* Section heading */}
          <Typography
            variant="overline"
            sx={{
              color: "#fff",
              fontSize: "10px",
              letterSpacing: "5px",
              fontWeight: "700",
            }}
          >
            Client Results – Real People, Real Progress
          </Typography>

          {/* Bold subheading */}
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: "#00ffff",
              mt: 2,
              mx: "auto",
              maxWidth: "80%",
              textAlign: "center",
              fontSize: {
                xs: "16px",
                sm: "18px",
                md: "20px",
                lg: "22px",
              },
              mb: 1,
            }}
          >
            This is what happens when structure + accountability replace guesswork.
          </Typography>

          {/* Main title */}
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              color: "#fff",
              mt: 1,
              mx: "auto",
              maxWidth: "70%",
              textAlign: "center",
              textTransform: "uppercase",
              fontSize: {
                xs: "22px",
                sm: "30px",
                md: "38px",
                lg: "42px",
                xl: "48px",
              },
            }}
          >
            Real People. Real Results. No Filters.
          </Typography>

          {/* Client testimonials list */}
          <Typography
            variant="body1"
            sx={{
              mt: 3,
              color: "#fff",
              maxWidth: "100%",
              display: "flex",
              justifyContent: "center",
              mx: "auto",
              fontSize: { xs: "14px", sm: "16px", md: "18px", lg: "20px" },
              fontWeight: 500,
              textAlign: "start",
              "& ul": {
                paddingLeft: "1.2rem",
                margin: 0,
                listStyleType: "none",
                position: "relative",
              },
              "& li": {
                color: "#fff",
                paddingBottom: { xs: "6px", sm: "8px", md: "10px" },
                fontWeight: { xs: 500, md: 500 },
                position: "relative",
                paddingLeft: { xs: "20px", sm: "24px", md: "28px" },
                "&:before": {
                  content: '""',
                  position: "absolute",
                  left: 0,
                  top: { xs: "8px", sm: "10px", md: "12px" },
                  width: { xs: "6px", sm: "7px", md: "8px" },
                  height: { xs: "6px", sm: "7px", md: "8px" },
                  backgroundColor: "#00ffff",
                  borderRadius: "50%",
                },
              },
            }}
          >
            <ul>
              <li>“Dropped 12kg without losing strength.”</li>
              <li>“Felt like I was 28 again at 41.”</li>
              <li>“The first time I’ve ever stuck to a system.”</li>
            </ul>
          </Typography>

          {/* Call-to-action button to join the movement */}
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#00ffff",
              color: "#111111",
              fontSize: {
                xs: "12px",
                sm: "12px",
                md: "12px",
                lg: "12px",
                xl: "12px",
              },
              marginTop: {
                xs: "12px",
                sm: "25px",
                md: "26px",
                lg: "20px",
              },
              fontWeight: 700,
              padding: {
                xs: "10px 20px",
                sm: "12px 24px",
                md: "13px 26px",
                lg: "14px 28px",
                xl: "15px 30px",
              },
              borderRadius: "25px",
              textTransform: "uppercase",
              letterSpacing: "1px",
              transition: "all 0.3s ease",
              minWidth: "auto",
              whiteSpace: "nowrap",
              "&:hover": {
                backgroundColor: "#00ffff",
                transform: "translateY(-2px)",
                boxShadow: "0 4px 12px rgba(0, 198, 194, 0.3)",
              },
            }}
            onClick={() => {
              const section = document.getElementById("subscription-section");
              if (section) {
                section.scrollIntoView({ behavior: "smooth" });
              }
            }}
          >
            Join the Recomp Movement
          </Button>

          {/* Image grid for transformation images */}
          <Grid
            container
            spacing={{ xs: 2, sm: 3 }}
            sx={{
              mt: 4,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {images.map((image, index) => (
              <Grid
                item
                key={index}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "300px",
                  height: {
                    xs: 250,
                    sm: 250,
                  },
                  borderRadius: "12px",
                  overflow: "hidden",
                  border: "2px solid #222",
                  aspectRatio: "1 / 1",
                }}
              >
                <Box
                  onClick={() => openGallery(index)}
                  sx={{
                    width: "100%",
                    height: {
                      xs: 160,
                      sm: 200,
                      md: 240,
                    },
                    borderRadius: "12px",
                    border: "2px solid #333",
                    aspectRatio: "4 / 3",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    overflow: "hidden",
                    position: "relative",
                    "&:hover": {
                      transform: "scale(1.05)",
                      border: "2px solid #00ffff",
                      boxShadow: "0 8px 25px rgba(0, 198, 194, 0.4)",
                      zIndex: 1,
                    },
                  }}
                  title={`Click to view ${image.alt}`}
                >
                  {/* Blurred background image for visual effect */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundImage: `url(${image.src})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      filter: "blur(8px) brightness(0.3)",
                      zIndex: 1,
                    }}
                  />

                  {/* Main image displayed over the blurred background */}
                  <Box
                    sx={{
                      position: "relative",
                      zIndex: 2,
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img
                      src={image.src}
                      alt={image.alt}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                        transition: "all 0.3s ease",
                        position: "relative",
                        zIndex: 2,
                      }}
                    />
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Secondary call-to-action button */}
          <Button
            variant="contained"
            sx={{
              mt: 5,
              backgroundColor: "#00ffff",
              color: "#000",
              borderRadius: "32px",
              px: 4,
              py: 1.5,
              fontWeight: 600,
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: "#00ffff",
                transform: "translateY(-2px)",
                boxShadow: "0 4px 12px rgba(0, 198, 194, 0.3)",
              },
            }}
            onClick={() => {
              const section = document.getElementById("subscription-section");
              if (section) {
                section.scrollIntoView({ behavior: "smooth" });
              }
            }}
          >
            COULD YOU BE NEXT?
          </Button>
        </Box>
      </Box>

      {/* Modal for full-screen image gallery */}
      <Modal
        open={galleryOpen}
        onClose={closeGallery}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
          sx: { backgroundColor: "rgba(0, 0, 0, 0.9)" },
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        <Box
          sx={{
            position: "relative",
            maxWidth: "90vw",
            maxHeight: "90vh",
            outline: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {selectedImage && (
            <>
              {/* Button to close the modal */}
              <IconButton
                onClick={closeGallery}
                sx={{
                  position: "absolute",
                  top: -50,
                  right: -50,
                  color: "#fff",
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                  zIndex: 1000,
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.9)",
                  },
                }}
              >
                <CloseIcon />
              </IconButton>

              {/* Button to navigate to the previous image */}
              <IconButton
                onClick={prevImage}
                sx={{
                  position: "absolute",
                  left: -60,
                  color: "#fff",
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                  zIndex: 1000,
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.9)",
                  },
                }}
              >
                <WestIcon />
              </IconButton>

              {/* Main image in the modal */}
              <img
                src={selectedImage.src}
                alt={selectedImage.alt}
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                  borderRadius: "8px",
                }}
              />

              {/* Button to navigate to the next image */}
              <IconButton
                onClick={nextImage}
                sx={{
                  position: "absolute",
                  right: -60,
                  color: "#fff",
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                  zIndex: 1000,
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.9)",
                  },
                }}
              >
                <EastIcon />
              </IconButton>

              {/* Image counter display */}
              <Box
                sx={{
                  position: "absolute",
                  bottom: -50,
                  left: "50%",
                  transform: "translateX(-50%)",
                  color: "#fff",
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                  px: 2,
                  py: 1,
                  borderRadius: "20px",
                  fontSize: "14px",
                }}
              >
                {currentImageIndex + 1} / {images.length}
              </Box>
            </>
          )}
        </Box>
      </Modal>
    </div>
  );
};

// Export the RealResult component
export default RealResult;